/**
 * Local utilities shim for @carvana/ds-utils
 *
 * Since @carvana/ds-utils is not available in the npm registry,
 * this file provides the minimal utilities needed by the DatePicker component.
 */

import { CSSProperties, createElement, forwardRef, ReactNode } from 'react';

export type ElementWithProps<T = Record<string, unknown>> = T & {
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
};

type PropsFunction<TProps> = (props: TProps) => {
  className?: string;
  style?: CSSProperties | Record<string, unknown>;
  [key: string]: unknown;
};

/**
 * Creates a component with automatic className and style handling
 */
export function createComponent<TProps = Record<string, unknown>>(
  element: string,
  propsOrFn:
    | { className?: string; style?: CSSProperties; [key: string]: unknown }
    | PropsFunction<TProps>
) {
  const Component = forwardRef<
    HTMLElement,
    TProps & { className?: string; style?: CSSProperties; children?: React.ReactNode }
  >((props, ref) => {
    const derivedProps = typeof propsOrFn === 'function' ? propsOrFn(props as TProps) : propsOrFn;

    const { className, style, ...rest } = props;
    const mergedClassName = [derivedProps.className, className].filter(Boolean).join(' ');
    const mergedStyle = { ...derivedProps.style, ...style };

    return createElement(
      element,
      {
        ...derivedProps,
        ...rest,
        ref,
        className: mergedClassName || undefined,
        style: Object.keys(mergedStyle).length > 0 ? mergedStyle : undefined
      },
      props.children
    );
  });
  Component.displayName = `DatePicker.${element}`;
  return Component;
}

/**
 * Box shadow utilities for focus states
 */
export const boxShadow = {
  focusVisible: '0 0 0 4px var(--border-primary-subtle)'
};
